import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_URL } from '../config/api';
import { adminFetch } from '../utils/adminFetch';
import DataTable from './common/DataTable';
import './Email.css';

// Convert markdown-style content to HTML (mirrors backend textToHtml)
const textToHtml = (text: string): string => {
  if (!text) return '';
  if (text.includes('<html') || text.includes('<div') || text.includes('<p>') || text.includes('<br')) {
    return text;
  }
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #10b981; text-decoration: underline;">$1</a>');
  html = html.replace(/(^|[^">])(https?:\/\/[^\s<]+)/g, '$1<a href="$2" style="color: #10b981; text-decoration: underline;">$2</a>');
  html = html.replace(/\n/g, '<br>\n');
  return html;
};

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

interface Campaign {
  _id: string;
  name: string;
  subject: string;
  content?: string;
  enabled: boolean;
  segmentId: string;
  segmentName?: string;
  couponCode?: string;  // Associated promo code
  scheduledAt?: string;
  sentAt?: string;
  stats: {
    totalRecipients: number;
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
  };
  actualUnsent?: number;  // Calculated from backend: segment - sent - unsubscribed
  actualTotal?: number;   // Current segment total (excluding unsubscribed)
  createdAt: string;
}

interface Segment {
  id: string;
  name: string;
  description: string;
  count?: number;
}

interface EmailLog {
  _id: string;
  email: string;
  dateSent: string;
  subject: string;
  content: string;
  status: 'sent' | 'delivered' | 'opened' | 'replied' | 'bounced' | 'failed';
  campaignId?: string;
  campaignName?: string;
  sendGridStatus?: string;
}

type TabType = 'campaigns' | 'segments' | 'logs' | 'coupons';

interface PromoCode {
  id: string;
  code: string;
  couponId: string;
  percentOff: number | null;
  amountOff: number | null;
  duration: string;
  active: boolean;
  maxRedemptions: number | null;
  timesRedeemed: number;
  createdAt: string;
  metadata?: Record<string, string>;
}

interface PromoCodeStats {
  code: string;
  couponId: string;
  percentOff: number | null;
  amountOff: number | null;
  duration: string;
  active: boolean;
  maxRedemptions: number | null;
  timesRedeemed: number;
  createdAt: string;
  redemptions: Array<{
    invoiceId: string;
    customerEmail: string;
    customerId: string;
    amountPaid: number;
    discountAmount: number;
    currency: string;
    date: string;
    subscriptionId: string;
    plan: string;
  }>;
  summary: {
    totalRedemptions: number;
    totalDiscountGiven: number;
    totalRevenue: number;
    currency: string;
  };
}

const Email: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as TabType | null;
  const [activeTab, setActiveTabState] = useState<TabType>(
    tabFromUrl && ['campaigns', 'segments', 'logs', 'coupons'].includes(tabFromUrl) ? tabFromUrl : 'campaigns'
  );
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [segmentsData, setSegmentsData] = useState<Segment[]>([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [segmentsRefreshing, setSegmentsRefreshing] = useState(false);
  const [segmentsCacheAge, setSegmentsCacheAge] = useState<number | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);

  // Sorting state for campaigns
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // Test email modal state
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailCampaignId, setTestEmailCampaignId] = useState<string | null>(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Single email modal state
  const [showSingleEmailModal, setShowSingleEmailModal] = useState(false);
  const [singleEmailCampaignId, setSingleEmailCampaignId] = useState<string | null>(null);
  const [singleEmailTarget, setSingleEmailTarget] = useState<{ email: string; name?: string } | null>(null);
  const [singleEmailLoading, setSingleEmailLoading] = useState(false);
  const [sendingSingleEmail, setSendingSingleEmail] = useState(false);
  const [singleEmailResult, setSingleEmailResult] = useState<{ success: boolean; message: string } | null>(null);

  // Edit campaign modal state
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editViewMode, setEditViewMode] = useState<'preview' | 'html'>('preview');

  // Logs tab state
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsSearch, setLogsSearch] = useState('');
  const [filteredLogs, setFilteredLogs] = useState<EmailLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);

  // Coupons tab state
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [promoCodesLoading, setPromoCodesLoading] = useState(false);
  const [selectedPromoCode, setSelectedPromoCode] = useState<PromoCodeStats | null>(null);
  const [promoCodeStatsLoading, setPromoCodeStatsLoading] = useState(false);

  // Sorting state for coupons
  const [couponSortColumn, setCouponSortColumn] = useState<string>('createdAt');
  const [couponSortDirection, setCouponSortDirection] = useState<'asc' | 'desc'>('desc');

  const setActiveTab = (tab: TabType) => {
    setActiveTabState(tab);
    setSearchParams({ tab });
  };

  useEffect(() => {
    if (activeTab === 'campaigns') {
      fetchCampaigns();
      fetchSegments(); // 캠페인 탭에서도 세그먼트 데이터 필요 (현재 모수 표시용)
    } else if (activeTab === 'segments') {
      fetchSegments(); // 백엔드에서 5분 캐시 + cron으로 갱신됨
    } else if (activeTab === 'logs') {
      fetchLogs();
    } else if (activeTab === 'coupons') {
      fetchPromoCodes();
    }
  }, [activeTab]);

  // Fetch campaign details when editing
  useEffect(() => {
    if (editingCampaign) {
      fetchCampaignDetails(editingCampaign._id);
    }
  }, [editingCampaign?._id]);

  const fetchCampaignDetails = async (campaignId: string) => {
    try {
      console.log('Fetching campaign details for:', campaignId);
      const response = await fetch(`${API_URL}/api/newsletter/campaigns/${campaignId}`, {
        headers: { ...getAuthHeaders() }
      });
      console.log('Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Campaign data:', data);
        setEditSubject(data.campaign.subject || '');
        setEditBody(data.campaign.content || '');
      } else {
        const errorData = await response.json();
        console.error('API error:', errorData);
      }
    } catch (error) {
      console.error('Failed to fetch campaign details:', error);
    }
  };

  const handleSaveCampaign = async () => {
    if (!editingCampaign) return;

    setSavingEdit(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/campaigns/${editingCampaign._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          subject: editSubject,
          content: editBody
        })
      });

      if (response.ok) {
        alert('Campaign updated successfully');
        setEditingCampaign(null);
        setEditSubject('');
        setEditBody('');
        fetchCampaigns();
      } else {
        const error = await response.json();
        alert(`Failed to update campaign: ${error.error}`);
      }
    } catch (error) {
      console.error('Failed to update campaign:', error);
      alert('Failed to update campaign');
    } finally {
      setSavingEdit(false);
    }
  };

  const fetchSegments = async () => {
    setSegmentsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/segments`, {
        headers: { ...getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        setSegmentsData(data.segments || []);
        setSegmentsCacheAge(data.cacheAge ?? null);
      }
    } catch (error) {
      console.error('Failed to fetch segments:', error);
    } finally {
      setSegmentsLoading(false);
    }
  };

  const refreshSegments = async () => {
    setSegmentsRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/segments/refresh`, {
        method: 'POST',
        headers: { ...getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        setSegmentsData(data.segments || []);
        setSegmentsCacheAge(0);
      }
    } catch (error) {
      console.error('Failed to refresh segments:', error);
    } finally {
      setSegmentsRefreshing(false);
    }
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/campaigns`, {
        headers: { ...getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        const campaignsList = data.campaigns || [];
        setCampaigns(campaignsList);

        // Load stats for each campaign asynchronously (after initial render)
        campaignsList.forEach((campaign: Campaign) => {
          fetchCampaignStats(campaign._id);
        });
      }
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch individual campaign stats asynchronously
  const fetchCampaignStats = async (campaignId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/newsletter/campaigns/${campaignId}/stats`, {
        headers: { ...getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.stats) {
          // Update campaign with stats
          setCampaigns(prev => prev.map(c =>
            c._id === campaignId
              ? {
                  ...c,
                  actualUnsent: data.stats.actualUnsent,
                  actualTotal: data.stats.actualTotal,
                  stats: { ...c.stats, sent: data.stats.sent }
                }
              : c
          ));
        }
      } else {
        console.error(`Stats API returned ${response.status} for campaign ${campaignId}`);
        // On failure, set default values so UI doesn't show ...
        setCampaigns(prev => prev.map(c =>
          c._id === campaignId
            ? { ...c, actualUnsent: 0, actualTotal: 0 }
            : c
        ));
      }
    } catch (error) {
      console.error(`Failed to fetch stats for campaign ${campaignId}:`, error);
      // On error, set default values so UI doesn't show ...
      setCampaigns(prev => prev.map(c =>
        c._id === campaignId
          ? { ...c, actualUnsent: 0, actualTotal: 0 }
          : c
      ));
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/logs?limit=500`, {
        headers: { ...getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.data || []);
        setFilteredLogs(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      setLogs([]);
      setFilteredLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  // Filter logs based on search
  useEffect(() => {
    if (!logsSearch) {
      setFilteredLogs(logs);
    } else {
      const searchLower = logsSearch.toLowerCase();
      const filtered = logs.filter(log =>
        log.email.toLowerCase().includes(searchLower) ||
        log.subject?.toLowerCase().includes(searchLower) ||
        log.status?.toLowerCase().includes(searchLower) ||
        log.campaignName?.toLowerCase().includes(searchLower)
      );
      setFilteredLogs(filtered);
    }
  }, [logsSearch, logs]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return '#10b981';
      case 'opened': return '#8b5cf6';
      case 'clicked': return '#f59e0b';
      case 'replied': return '#06b6d4';
      case 'bounced': return '#ef4444';
      case 'failed': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // Sort campaigns
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const sortedCampaigns = [...campaigns].sort((a, b) => {
    let aVal: any, bVal: any;

    switch (sortColumn) {
      case 'subject':
        aVal = a.subject?.toLowerCase() || '';
        bVal = b.subject?.toLowerCase() || '';
        break;
      case 'segment':
        aVal = a.segmentName?.toLowerCase() || a.segmentId?.toLowerCase() || '';
        bVal = b.segmentName?.toLowerCase() || b.segmentId?.toLowerCase() || '';
        break;
      case 'coupon':
        aVal = a.couponCode?.toLowerCase() || '';
        bVal = b.couponCode?.toLowerCase() || '';
        break;
      case 'total':
        aVal = a.actualTotal ?? a.stats?.totalRecipients ?? 0;
        bVal = b.actualTotal ?? b.stats?.totalRecipients ?? 0;
        break;
      case 'sent':
        aVal = a.stats?.sent || 0;
        bVal = b.stats?.sent || 0;
        break;
      case 'unsent':
        aVal = a.actualUnsent ?? Math.max(0, (a.actualTotal ?? a.stats?.totalRecipients ?? 0) - (a.stats?.sent || 0));
        bVal = b.actualUnsent ?? Math.max(0, (b.actualTotal ?? b.stats?.totalRecipients ?? 0) - (b.stats?.sent || 0));
        break;
      case 'opened':
        const aSent = a.stats?.sent || 0;
        const bSent = b.stats?.sent || 0;
        aVal = aSent > 0 ? (a.stats?.opened || 0) / aSent : 0;
        bVal = bSent > 0 ? (b.stats?.opened || 0) / bSent : 0;
        break;
      case 'clicked':
        const aOpened = a.stats?.opened || 0;
        const bOpened = b.stats?.opened || 0;
        aVal = aOpened > 0 ? (a.stats?.clicked || 0) / aOpened : 0;
        bVal = bOpened > 0 ? (b.stats?.clicked || 0) / bOpened : 0;
        break;
      case 'createdAt':
      default:
        aVal = new Date(a.createdAt).getTime();
        bVal = new Date(b.createdAt).getTime();
        break;
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const renderSortHeader = (column: string, label: string) => (
    <th
      key={column}
      onClick={() => handleSort(column)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {label}
      {sortColumn === column && (
        <span style={{ marginLeft: '4px', opacity: 0.7 }}>
          {sortDirection === 'asc' ? '▲' : '▼'}
        </span>
      )}
    </th>
  );

  // Sort coupons
  const handleCouponSort = (column: string) => {
    if (couponSortColumn === column) {
      setCouponSortDirection(couponSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setCouponSortColumn(column);
      setCouponSortDirection('desc');
    }
  };

  const sortedPromoCodes = [...promoCodes].sort((a, b) => {
    let aVal: any, bVal: any;

    switch (couponSortColumn) {
      case 'code':
        aVal = a.code?.toLowerCase() || '';
        bVal = b.code?.toLowerCase() || '';
        break;
      case 'discount':
        aVal = a.percentOff || a.amountOff || 0;
        bVal = b.percentOff || b.amountOff || 0;
        break;
      case 'duration':
        aVal = a.duration || '';
        bVal = b.duration || '';
        break;
      case 'used':
        aVal = a.timesRedeemed || 0;
        bVal = b.timesRedeemed || 0;
        break;
      case 'maxUses':
        aVal = a.maxRedemptions || Infinity;
        bVal = b.maxRedemptions || Infinity;
        break;
      case 'status':
        aVal = a.active ? 1 : 0;
        bVal = b.active ? 1 : 0;
        break;
      case 'createdAt':
      default:
        aVal = new Date(a.createdAt).getTime();
        bVal = new Date(b.createdAt).getTime();
        break;
    }

    if (aVal < bVal) return couponSortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return couponSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const renderCouponSortHeader = (column: string, label: string) => (
    <th
      key={column}
      onClick={() => handleCouponSort(column)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {label}
      {couponSortColumn === column && (
        <span style={{ marginLeft: '4px', opacity: 0.7 }}>
          {couponSortDirection === 'asc' ? '▲' : '▼'}
        </span>
      )}
    </th>
  );

  // Fetch promo codes
  const fetchPromoCodes = async () => {
    setPromoCodesLoading(true);
    try {
      const response = await adminFetch(`${API_URL}/api/stripe/promo-codes?limit=50`);
      if (response.ok) {
        const data = await response.json();
        setPromoCodes(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch promo codes:', error);
      setPromoCodes([]);
    } finally {
      setPromoCodesLoading(false);
    }
  };

  // Fetch promo code stats
  const fetchPromoCodeStats = async (code: string) => {
    setPromoCodeStatsLoading(true);
    try {
      const response = await adminFetch(`${API_URL}/api/stripe/promo-codes/${code}/stats`);
      if (response.ok) {
        const data = await response.json();
        setSelectedPromoCode(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch promo code stats:', error);
    } finally {
      setPromoCodeStatsLoading(false);
    }
  };

  const handleSendCampaign = async (campaignId: string) => {
    if (!window.confirm('Are you sure you want to send this campaign? This will send emails to all recipients in the segment.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/newsletter/campaigns/${campaignId}/send`, {
        method: 'POST',
        headers: { ...getAuthHeaders() }
      });

      if (response.ok) {
        alert('Campaign sending started!');
        fetchCampaigns();
      } else {
        const error = await response.json();
        alert(`Failed to send campaign: ${error.error}`);
      }
    } catch (error) {
      console.error('Failed to send campaign:', error);
      alert('Failed to send campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/newsletter/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders() }
      });

      if (response.ok) {
        fetchCampaigns();
      } else {
        const error = await response.json();
        alert(`Failed to delete campaign: ${error.error}`);
      }
    } catch (error) {
      console.error('Failed to delete campaign:', error);
    }
  };

  const handleToggleCampaign = async (campaignId: string) => {
    // Optimistic update - update UI immediately
    setCampaigns(prev => prev.map(c =>
      c._id === campaignId ? { ...c, enabled: !c.enabled } : c
    ));

    try {
      const response = await fetch(`${API_URL}/api/newsletter/campaigns/${campaignId}/toggle`, {
        method: 'PUT',
        headers: { ...getAuthHeaders() }
      });
      if (!response.ok) {
        // Revert on error
        setCampaigns(prev => prev.map(c =>
          c._id === campaignId ? { ...c, enabled: !c.enabled } : c
        ));
        const error = await response.json();
        alert(`Failed to toggle campaign: ${error.error}`);
      }
    } catch (error) {
      // Revert on error
      setCampaigns(prev => prev.map(c =>
        c._id === campaignId ? { ...c, enabled: !c.enabled } : c
      ));
      console.error('Failed to toggle campaign:', error);
    }
  };

  // Open test email modal
  const openTestEmailModal = (campaignId: string) => {
    setTestEmailCampaignId(campaignId);
    setTestEmailAddress('');
    setShowTestEmailModal(true);
  };

  // Send test email
  const handleSendTestEmail = async () => {
    if (!testEmailCampaignId || !testEmailAddress.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    setSendingTestEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/campaigns/${testEmailCampaignId}/send-single`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ email: testEmailAddress, isTest: true })
      });

      if (response.ok) {
        alert(`Test email sent to ${testEmailAddress}`);
        setShowTestEmailModal(false);
        setTestEmailAddress('');
        setTestEmailCampaignId(null);
      } else {
        const error = await response.json();
        alert(`Failed to send: ${error.error}`);
      }
    } catch (error) {
      console.error('Failed to send test email:', error);
      alert('Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Open single email modal and fetch next unsent target
  const openSingleEmailModal = async (campaignId: string) => {
    setSingleEmailCampaignId(campaignId);
    setSingleEmailTarget(null);
    setSingleEmailResult(null);
    setSingleEmailLoading(true);
    setShowSingleEmailModal(true);

    try {
      const response = await fetch(`${API_URL}/api/newsletter/campaigns/${campaignId}/next-unsent`, {
        headers: { ...getAuthHeaders() }
      });

      const data = await response.json();

      if (data.success && data.target) {
        setSingleEmailTarget(data.target);
      } else {
        setSingleEmailTarget(null);
        if (data.message) {
          alert(data.message);
        }
      }
    } catch (error) {
      console.error('Error fetching next unsent target:', error);
      alert('Failed to fetch target');
    } finally {
      setSingleEmailLoading(false);
    }
  };

  // Send single email to the target
  const handleSendSingleEmail = async () => {
    if (!singleEmailCampaignId || !singleEmailTarget) return;

    setSendingSingleEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/campaigns/${singleEmailCampaignId}/send-single`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ email: singleEmailTarget.email })
      });

      const data = await response.json();

      if (response.ok) {
        setSingleEmailResult({ success: true, message: `Email sent to ${singleEmailTarget.email}` });
        fetchCampaigns(); // Refresh stats
      } else {
        setSingleEmailResult({ success: false, message: data.error || 'Failed to send' });
      }
    } catch (error) {
      console.error('Failed to send single email:', error);
      setSingleEmailResult({ success: false, message: 'Failed to send email' });
    } finally {
      setSendingSingleEmail(false);
    }
  };

  const formatNumber = (num: number) => num?.toLocaleString() || '0';
  const formatDate = (date: string) => new Date(date).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="email-container">
      <div className="email-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>Email Marketing</h1>
        </div>
        <button
          onClick={refreshSegments}
          disabled={segmentsRefreshing}
          style={{
            padding: '8px 16px',
            backgroundColor: segmentsRefreshing ? '#ccc' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: segmentsRefreshing ? 'not-allowed' : 'pointer',
            fontSize: '13px'
          }}
        >
          {segmentsRefreshing ? 'Updating...' : 'Update Segments'}
        </button>
      </div>

      <div className="email-tabs">
        <button
          className={`email-tab ${activeTab === 'campaigns' ? 'active' : ''}`}
          onClick={() => setActiveTab('campaigns')}
        >
          Campaigns
        </button>
        <button
          className={`email-tab ${activeTab === 'segments' ? 'active' : ''}`}
          onClick={() => setActiveTab('segments')}
        >
          Segments
        </button>
        <button
          className={`email-tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          Logs
        </button>
        <button
          className={`email-tab ${activeTab === 'coupons' ? 'active' : ''}`}
          onClick={() => setActiveTab('coupons')}
        >
          Coupons
        </button>
      </div>

      <div className="email-content">
        {activeTab === 'campaigns' && (
          <div className="campaigns-list">
            {loading ? (
              <div className="loading">Loading campaigns...</div>
            ) : campaigns.length === 0 ? (
              <div className="empty-state">
                <p>No campaigns yet.</p>
              </div>
            ) : (
              <table className="campaigns-table">
                <thead>
                  <tr>
                    {renderSortHeader('subject', 'Subject')}
                    {renderSortHeader('segment', 'Segment')}
                    {renderSortHeader('coupon', 'Coupon')}
                    {renderSortHeader('total', 'Total Target')}
                    {renderSortHeader('sent', 'Sent')}
                    {renderSortHeader('unsent', 'Unsent')}
                    {renderSortHeader('opened', 'Opened')}
                    {renderSortHeader('clicked', 'Clicked')}
                    {renderSortHeader('createdAt', 'Created')}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCampaigns.map(campaign => {
                    // Use backend-calculated values (accurate) or fallback to segment data
                    const statsLoading = campaign.actualTotal === null || campaign.actualUnsent === null;
                    const totalTarget = campaign.actualTotal ?? campaign.stats?.totalRecipients ?? 0;
                    const sent = campaign.stats?.sent || 0;
                    const unsent = campaign.actualUnsent ?? Math.max(0, totalTarget - sent);

                    return (
                    <tr key={campaign._id}>
                      <td>
                        <span
                          style={{
                            cursor: 'pointer',
                            color: 'var(--color-primary)',
                            textDecoration: 'underline'
                          }}
                          onClick={() => setEditingCampaign(campaign)}
                          title="Click to edit subject and body"
                        >
                          {campaign.subject}
                        </span>
                      </td>
                      <td>{campaign.segmentName || campaign.segmentId}</td>
                      <td>
                        {campaign.couponCode ? (
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            backgroundColor: '#8b5cf620',
                            color: '#8b5cf6',
                            fontWeight: 500
                          }}>
                            {campaign.couponCode}
                          </span>
                        ) : '-'}
                      </td>
                      <td>{statsLoading ? <span style={{ color: '#999' }}>...</span> : formatNumber(totalTarget)}</td>
                      <td style={{ color: sent > 0 ? '#28a745' : 'inherit' }}>{statsLoading ? <span style={{ color: '#999' }}>...</span> : formatNumber(sent)}</td>
                      <td style={{ color: unsent > 0 ? '#dc3545' : '#28a745' }}>{statsLoading ? <span style={{ color: '#999' }}>...</span> : formatNumber(unsent)}</td>
                      <td>
                        {sent > 0
                          ? `${((campaign.stats.opened / sent) * 100).toFixed(1)}%`
                          : '-'}
                      </td>
                      <td>
                        {campaign.stats.opened > 0
                          ? `${((campaign.stats.clicked / campaign.stats.opened) * 100).toFixed(1)}%`
                          : '-'}
                      </td>
                      <td>{formatDate(campaign.createdAt)}</td>
                      <td>
                        <div className="campaign-actions" style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => openTestEmailModal(campaign._id)}
                            style={{ backgroundColor: 'var(--color-primary)', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}
                            title="Send test email to yourself"
                          >
                            Test
                          </button>
                          <button
                            onClick={() => openSingleEmailModal(campaign._id)}
                            style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}
                            title="Send to one unsent recipient"
                          >
                            Single
                          </button>
                          <button
                            onClick={() => handleSendCampaign(campaign._id)}
                            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}
                            title="Send to all unsent recipients"
                          >
                            Send All
                          </button>
                          <button
                            onClick={() => setSelectedCampaign(campaign)}
                            style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}
                            title="View statistics"
                          >
                            Stats
                          </button>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '4px' }}>
                            <button
                              onClick={() => handleToggleCampaign(campaign._id)}
                              className={`ios-toggle ${campaign.enabled ? 'enabled' : ''}`}
                              title={campaign.enabled ? 'Auto-send enabled' : 'Auto-send disabled'}
                            >
                              <span className="ios-toggle-slider" />
                            </button>
                            <span style={{ fontSize: '11px', color: campaign.enabled ? '#4CD964' : '#666', minWidth: '28px' }}>
                              {campaign.enabled ? 'Auto' : 'Off'}
                            </span>
                          </div>
                          <button
                            onClick={() => handleDeleteCampaign(campaign._id)}
                            style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}
                            title="Delete campaign"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'segments' && (
          <div className="segments-list">
            {segmentsCacheAge !== null && (
              <div style={{ marginBottom: '12px', color: '#666', fontSize: '13px' }}>
                Last updated: {segmentsCacheAge < 60 ? `${segmentsCacheAge}s` : `${Math.floor(segmentsCacheAge / 60)}m`} ago
              </div>
            )}
            {segmentsLoading ? (
              <div className="loading">Loading segments...</div>
            ) : (
              <div className="segments-grid">
                {segmentsData.map(segment => (
                  <div key={segment.id} className="segment-card">
                    <h3>{segment.name}</h3>
                    <div className="segment-count">
                      {segment.count?.toLocaleString() || 0}
                    </div>
                    <p className="segment-description">{segment.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="logs-section">
            {/* Search and Refresh */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search by email, subject, status, campaign..."
                value={logsSearch}
                onChange={(e) => setLogsSearch(e.target.value)}
                style={{
                  flex: 1,
                  minWidth: '300px',
                  padding: '10px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <button
                onClick={fetchLogs}
                style={{
                  padding: '10px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  background: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                Refresh
              </button>
            </div>

            {/* Logs Table */}
            {logsLoading ? (
              <div className="loading">Loading logs...</div>
            ) : filteredLogs.length === 0 ? (
              <div className="empty-state">
                <p>No email logs found</p>
                {logsSearch && <p style={{ fontSize: '14px', color: '#666' }}>Try adjusting your search criteria</p>}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="campaigns-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Recipient</th>
                      <th>Subject</th>
                      <th>Campaign</th>
                      <th>Status</th>
                      <th>Content</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map(log => (
                      <tr key={log._id}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {new Date(log.dateSent).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td>{log.email}</td>
                        <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.subject}
                        </td>
                        <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.campaignName || '-'}
                        </td>
                        <td>
                          <span style={{
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '13px',
                            fontWeight: 500,
                            backgroundColor: `${getStatusColor(log.sendGridStatus || log.status)}20`,
                            color: getStatusColor(log.sendGridStatus || log.status)
                          }}>
                            {log.sendGridStatus || log.status}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => setSelectedLog(log)}
                            style={{
                              padding: '8px 16px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '4px',
                              background: 'white',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'coupons' && (
          <div className="coupons-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Promotion Codes</h3>
              <button
                onClick={fetchPromoCodes}
                style={{
                  padding: '10px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                Refresh
              </button>
            </div>

            {promoCodesLoading ? (
              <div className="loading">Loading promo codes...</div>
            ) : promoCodes.length === 0 ? (
              <div className="empty-state">
                <p>No promotion codes found</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="campaigns-table">
                  <thead>
                    <tr>
                      {renderCouponSortHeader('code', 'Code')}
                      {renderCouponSortHeader('discount', 'Discount')}
                      {renderCouponSortHeader('duration', 'Duration')}
                      {renderCouponSortHeader('used', 'Used')}
                      {renderCouponSortHeader('maxUses', 'Max Uses')}
                      {renderCouponSortHeader('status', 'Status')}
                      {renderCouponSortHeader('createdAt', 'Created')}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPromoCodes.map(promo => (
                      <tr key={promo.id}>
                        <td style={{ fontWeight: 600, fontFamily: 'monospace' }}>{promo.code}</td>
                        <td>
                          {promo.percentOff ? `${promo.percentOff}% off` : promo.amountOff ? `$${promo.amountOff} off` : '-'}
                        </td>
                        <td>{promo.duration}</td>
                        <td style={{ fontWeight: 600 }}>{promo.timesRedeemed}</td>
                        <td>{promo.maxRedemptions || 'Unlimited'}</td>
                        <td>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 500,
                            backgroundColor: promo.active ? '#10b98120' : '#ef444420',
                            color: promo.active ? '#10b981' : '#ef4444'
                          }}>
                            {promo.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                          {new Date(promo.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </td>
                        <td>
                          <button
                            onClick={() => fetchPromoCodeStats(promo.code)}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '4px',
                              background: 'white',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            View Stats
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Promo Code Stats Modal */}
      {selectedPromoCode && (
        <div className="modal-overlay" onClick={() => setSelectedPromoCode(null)}>
          <div className="modal-content stats-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>Coupon Statistics: {selectedPromoCode.code}</h2>
              <button className="modal-close" onClick={() => setSelectedPromoCode(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {promoCodeStatsLoading ? (
                <div className="loading">Loading stats...</div>
              ) : (
                <>
                  <div className="stats-grid modal-stats" style={{ marginBottom: '24px' }}>
                    <div className="stat-item">
                      <div className="stat-number">{selectedPromoCode.timesRedeemed}</div>
                      <div className="stat-label">Total Uses</div>
                    </div>
                    <div className="stat-item highlight">
                      <div className="stat-number">${selectedPromoCode.summary.totalRevenue.toFixed(2)}</div>
                      <div className="stat-label">Revenue Generated</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">${selectedPromoCode.summary.totalDiscountGiven.toFixed(2)}</div>
                      <div className="stat-label">Total Discount Given</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">{selectedPromoCode.percentOff || 0}%</div>
                      <div className="stat-label">Discount Rate</div>
                    </div>
                  </div>

                  <h4 style={{ marginBottom: '12px' }}>Redemption History ({selectedPromoCode.redemptions.length})</h4>
                  {selectedPromoCode.redemptions.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666', background: '#f9f9f9', borderRadius: '8px' }}>
                      No redemptions yet
                    </div>
                  ) : (
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <table className="campaigns-table" style={{ fontSize: '13px' }}>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Customer</th>
                            <th>Plan</th>
                            <th>Paid</th>
                            <th>Discount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedPromoCode.redemptions.map((r, i) => (
                            <tr key={i}>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {new Date(r.date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </td>
                              <td>{r.customerEmail}</td>
                              <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {r.plan}
                              </td>
                              <td style={{ fontWeight: 600, color: '#10b981' }}>${r.amountPaid.toFixed(2)}</td>
                              <td style={{ color: '#ef4444' }}>-${r.discountAmount.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSelectedPromoCode(null)}
                style={{ padding: '10px 20px', border: '1px solid #ccc', borderRadius: '6px', background: 'white', cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Stats Modal */}
      {selectedCampaign && (
        <div className="modal-overlay" onClick={() => setSelectedCampaign(null)}>
          <div className="modal-content stats-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Campaign Statistics</h2>
              <button className="modal-close" onClick={() => setSelectedCampaign(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="campaign-info">
                <h3>{selectedCampaign.name}</h3>
                <p className="subject">{selectedCampaign.subject}</p>
                <p className="meta">
                  Sent to {selectedCampaign.segmentName} on {formatDate(selectedCampaign.sentAt || selectedCampaign.createdAt)}
                </p>
              </div>

              <div className="stats-grid modal-stats">
                <div className="stat-item">
                  <div className="stat-number">{formatNumber(selectedCampaign.stats?.totalRecipients || 0)}</div>
                  <div className="stat-label">Recipients</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{formatNumber(selectedCampaign.stats?.delivered || 0)}</div>
                  <div className="stat-label">Delivered</div>
                  <div className="stat-percent">
                    {selectedCampaign.stats?.totalRecipients > 0
                      ? `${((selectedCampaign.stats.delivered / selectedCampaign.stats.totalRecipients) * 100).toFixed(1)}%`
                      : '-'}
                  </div>
                </div>
                <div className="stat-item highlight">
                  <div className="stat-number">{formatNumber(selectedCampaign.stats?.opened || 0)}</div>
                  <div className="stat-label">Opened</div>
                  <div className="stat-percent">
                    {selectedCampaign.stats?.delivered > 0
                      ? `${((selectedCampaign.stats.opened / selectedCampaign.stats.delivered) * 100).toFixed(1)}%`
                      : '-'}
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{formatNumber(selectedCampaign.stats?.clicked || 0)}</div>
                  <div className="stat-label">Clicked</div>
                  <div className="stat-percent">
                    {selectedCampaign.stats?.opened > 0
                      ? `${((selectedCampaign.stats.clicked / selectedCampaign.stats.opened) * 100).toFixed(1)}%`
                      : '-'}
                  </div>
                </div>
                <div className="stat-item error">
                  <div className="stat-number">{formatNumber(selectedCampaign.stats?.bounced || 0)}</div>
                  <div className="stat-label">Bounced</div>
                  <div className="stat-percent">
                    {selectedCampaign.stats?.totalRecipients > 0
                      ? `${((selectedCampaign.stats.bounced / selectedCampaign.stats.totalRecipients) * 100).toFixed(1)}%`
                      : '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestEmailModal && (
        <div className="modal-overlay" onClick={() => setShowTestEmailModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>Send Test Email</h2>
              <button className="modal-close" onClick={() => setShowTestEmailModal(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <p style={{ marginBottom: '16px', color: '#666' }}>
                Send a test email to verify the content looks correct. This won't count towards campaign stats.
              </p>
              <input
                type="email"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                placeholder="Enter your email address..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '16px'
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSendTestEmail()}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  onClick={() => setShowTestEmailModal(false)}
                  style={{ padding: '10px 20px', border: '1px solid #ccc', borderRadius: '6px', background: 'white', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendTestEmail}
                  disabled={sendingTestEmail || !testEmailAddress.includes('@')}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '6px',
                    background: 'var(--color-primary)',
                    color: 'white',
                    cursor: sendingTestEmail ? 'not-allowed' : 'pointer',
                    opacity: sendingTestEmail || !testEmailAddress.includes('@') ? 0.6 : 1
                  }}
                >
                  {sendingTestEmail ? 'Sending...' : 'Send Test'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Single Email Modal */}
      {showSingleEmailModal && (
        <div className="modal-overlay" onClick={() => setShowSingleEmailModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h2>Send Single Email</h2>
              <button className="modal-close" onClick={() => setShowSingleEmailModal(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {singleEmailLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p>Finding next unsent recipient...</p>
                </div>
              ) : singleEmailResult ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '16px'
                  }}>
                    {singleEmailResult.success ? '✅' : '❌'}
                  </div>
                  <p style={{ fontSize: '16px', color: singleEmailResult.success ? '#28a745' : '#dc3545' }}>
                    {singleEmailResult.message}
                  </p>
                  <button
                    onClick={() => {
                      setSingleEmailResult(null);
                      openSingleEmailModal(singleEmailCampaignId!);
                    }}
                    style={{
                      marginTop: '16px',
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#10b981',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Send Another
                  </button>
                </div>
              ) : singleEmailTarget ? (
                <div>
                  <p style={{ marginBottom: '16px', color: '#666' }}>
                    Send the campaign email to this recipient:
                  </p>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    marginBottom: '20px'
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: 500 }}>
                      {singleEmailTarget.email}
                    </div>
                    {singleEmailTarget.name && (
                      <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                        {singleEmailTarget.name}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button
                      onClick={() => setShowSingleEmailModal(false)}
                      style={{ padding: '10px 20px', border: '1px solid #ccc', borderRadius: '6px', background: 'white', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendSingleEmail}
                      disabled={sendingSingleEmail}
                      style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '6px',
                        background: '#10b981',
                        color: 'white',
                        cursor: sendingSingleEmail ? 'not-allowed' : 'pointer',
                        opacity: sendingSingleEmail ? 0.6 : 1
                      }}
                    >
                      {sendingSingleEmail ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                  <p style={{ fontSize: '16px', color: '#28a745' }}>
                    All recipients have been sent!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Campaign Modal */}
      {editingCampaign && (
        <div className="modal-overlay" onClick={() => {
          setEditingCampaign(null);
          setEditSubject('');
          setEditBody('');
          setEditViewMode('preview');
        }}>
          <div className="modal-content edit-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', width: '95%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h2>Edit Campaign</h2>
              <button className="modal-close" onClick={() => {
                setEditingCampaign(null);
                setEditSubject('');
                setEditBody('');
                setEditViewMode('preview');
              }}>&times;</button>
            </div>
            <div className="modal-body" style={{ padding: '20px', flex: 1, overflow: 'auto' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Subject</label>
                <input
                  type="text"
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter email subject..."
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontWeight: 500 }}>Body</label>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => setEditViewMode('preview')}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px 0 0 4px',
                        background: editViewMode === 'preview' ? 'var(--color-primary)' : 'white',
                        color: editViewMode === 'preview' ? 'white' : 'inherit',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Preview
                    </button>
                    <button
                      onClick={() => setEditViewMode('html')}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid var(--border-color)',
                        borderLeft: 'none',
                        borderRadius: '0 4px 4px 0',
                        background: editViewMode === 'html' ? 'var(--color-primary)' : 'white',
                        color: editViewMode === 'html' ? 'white' : 'inherit',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      HTML
                    </button>
                  </div>
                </div>
                {editViewMode === 'html' ? (
                  <textarea
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: '400px',
                      padding: '12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontFamily: 'monospace',
                      resize: 'vertical',
                      lineHeight: '1.5'
                    }}
                    placeholder="Enter email body (HTML supported)..."
                  />
                ) : (
                  <div
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => setEditBody(e.currentTarget.innerHTML)}
                    style={{
                      width: '100%',
                      minHeight: '400px',
                      maxHeight: '500px',
                      overflow: 'auto',
                      padding: '20px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      backgroundColor: '#fff',
                      outline: 'none',
                      cursor: 'text'
                    }}
                    dangerouslySetInnerHTML={{ __html: textToHtml(editBody) || '<p style="color: #999; font-style: italic;">Click here to edit...</p>' }}
                  />
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  className="action-btn"
                  onClick={() => {
                    setEditingCampaign(null);
                    setEditSubject('');
                    setEditBody('');
                  }}
                  style={{ padding: '10px 20px' }}
                >
                  Cancel
                </button>
                <button
                  className="action-btn send"
                  onClick={handleSaveCampaign}
                  disabled={savingEdit}
                  style={{ padding: '10px 20px' }}
                >
                  {savingEdit ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Log Content Modal */}
      {selectedLog && (
        <div className="modal-overlay" onClick={() => setSelectedLog(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>Email Details</h2>
              <button className="modal-close" onClick={() => setSelectedLog(null)}>&times;</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {/* Email Metadata */}
              <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>To</label>
                  <div style={{ fontWeight: 500 }}>{selectedLog.email}</div>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Date Sent</label>
                  <div style={{ fontWeight: 500 }}>
                    {new Date(selectedLog.dateSent).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Status</label>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 500,
                    backgroundColor: `${getStatusColor(selectedLog.sendGridStatus || selectedLog.status)}20`,
                    color: getStatusColor(selectedLog.sendGridStatus || selectedLog.status)
                  }}>
                    {selectedLog.sendGridStatus || selectedLog.status}
                  </span>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Campaign</label>
                  <div style={{ fontWeight: 500 }}>{selectedLog.campaignName || '-'}</div>
                </div>
              </div>

              {/* Subject */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '8px' }}>Subject</label>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600
                }}>
                  {selectedLog.subject}
                </div>
              </div>

              {/* Content */}
              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '8px' }}>Content</label>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  minHeight: '200px',
                  lineHeight: '1.6'
                }}>
                  {selectedLog.content?.includes('<') ? (
                    <div dangerouslySetInnerHTML={{ __html: selectedLog.content }} />
                  ) : (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{selectedLog.content || 'No content'}</div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSelectedLog(null)}
                style={{ padding: '10px 20px', border: '1px solid #ccc', borderRadius: '6px', background: 'white', cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Email;
